import { memo, useState, useCallback } from 'react';
import { TaskTodo, InitTodoForm } from 'models';
import { useNavigation } from 'hooks';

import { TodoEditForm, TodoCreateForm } from 'containers';

import Box from '@material-ui/core/Box';
import { IconButton, CaseIcon } from 'components';
import TaskWidgetItem from './task-widget-item';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import clsx from 'clsx';
import { useStyles } from './style';

interface Props {
  tasks: TaskTodo[];
  fetchTasks: (successCb?: () => void) => Promise<void>;
}

const TaskWidget: React.FC<Props> = ({ tasks, fetchTasks }) => {
  const classes = useStyles();
  const {
    data: slicedTasks,
    showPrevPage,
    showNextPage,
    disableNextButton,
    disabledPrevButton,
    totalPages,
    currentPage,
  } = useNavigation(tasks);

  const [taskId, selectTaskId] = useState<string | null>(null);
  const [openTodoCreateModal, setOpenTodoCreateModal] = useState(false);
  const [initForm, setInitForm] = useState<InitTodoForm | null>(null);

  const handleOpenEditModal = useCallback(
    (task: TaskTodo) => selectTaskId(task.Id),
    []
  );

  const handleCloseEditModal = useCallback(
    (update?: boolean, cb?: () => void) => {
      selectTaskId(null);

      if (update) fetchTasks(cb);
    },
    []
  );

  const handleOpenCreateModal = () => setOpenTodoCreateModal(true);
  const handleCloseCreateModal = (update?: boolean) => {
    setOpenTodoCreateModal(false);

    if (update) {
      fetchTasks();
    }
  };

  const handleSetInitTodoForm = useCallback(async (initForm: InitTodoForm) => {
    setInitForm(initForm);

    handleCloseCreateModal(true);

    setTimeout(() => {
      handleOpenCreateModal();
    }, 2500);

    /* eslint-disable-next-line */
  }, []);

  const handleSetInitEditTodoForm = useCallback((initForm: InitTodoForm) => {
    setInitForm(initForm);

    handleCloseEditModal(true, handleOpenCreateModal);
  }, []);

  const renderSlider = () => {
    const showButtons = totalPages > 1;

    return (
      <Box
        display="flex"
        alignItems="center"
        padding={{ xs: '0 2px 30px', sm: 0 }}
        width="100%"
        height="100%"
      >
        {showButtons && (
          <IconButton
            className={classes.iconBtn}
            onClick={showPrevPage}
            disabled={disabledPrevButton}
          >
            <ChevronLeftIcon className={classes.arrowIcon} />
          </IconButton>
        )}

        <Box
          width={`calc(100% - ${showButtons ? 60 : 10}px)`}
          margin="0 5px"
          data-testid="items-wrapper"
        >
          {slicedTasks.map((task) => (
            <TaskWidgetItem
              key={task.Id}
              task={task}
              handleClickItem={handleOpenEditModal}
            />
          ))}
        </Box>

        {showButtons && (
          <IconButton
            className={classes.iconBtn}
            onClick={showNextPage}
            disabled={disableNextButton}
          >
            <ChevronRightIcon className={classes.arrowIcon} />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderEmtpyWidget = () => (
    <>
      <CaseIcon />
      <Box ml={2}>
        <span className={clsx(classes.text, classes.bold)}>
          Tässä näet avoimet tehtävät.
        </span>
        <span className={clsx(classes.text, classes.greyish)}>
          Ei tehtäviä tässä kuussa.
        </span>
      </Box>
    </>
  );

  const showTasks = !!tasks.length;

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginBottom="15px"
        padding="15px"
      >
        <h3 className={classes.title}>
          {showTasks
            ? `Tehtävät tässä kuussa ${currentPage + 1}/${tasks.length}`
            : 'Tehtävät'}
        </h3>
      </Box>
      <Box
        position="relative"
        display="flex"
        alignItems="center"
        height="100%"
        padding={{
          xs: `${showTasks ? '0px 2px' : '30px 10px'} 30px`,
          sm: showTasks ? '0px 10px 30px' : '0px 30px 30px',
        }}
      >
        {showTasks ? renderSlider() : renderEmtpyWidget()}
      </Box>
      {taskId ? (
        <TodoEditForm
          handleSetInitTodoForm={handleSetInitEditTodoForm}
          taskId={taskId}
          handleClose={handleCloseEditModal}
        />
      ) : null}
      {openTodoCreateModal ? (
        <TodoCreateForm
          handleClose={handleCloseCreateModal}
          initForm={initForm}
          handleSetInitTodoForm={handleSetInitTodoForm}
        />
      ) : null}
    </>
  );
};

export default memo(TaskWidget);
